import { DateTime } from 'luxon'

import { Order, PackageEventOperations } from '@consumer/graphql'

import { PackageDeliveryIssueV2, PackageDelayedIssue, packageEvents } from '../enums'

/**
 * Returns true if any package in the order has been delivered or picked up from client.
 */
export const isDelivered = (order: Order): boolean => {
  if (order?.packages?.length) {
    const lastEventsFromAllPackages = order.packages.map(p => p.eventLog[0]?.operation)
    return (
      lastEventsFromAllPackages.includes(packageEvents.DELIVERED as PackageEventOperations) ||
      lastEventsFromAllPackages.includes(packageEvents.PICKED_UP_FROM_CLIENT as PackageEventOperations)
    )
  }
  return false
}

/**
 * Returns true if any package in the order was misdelivered to a wrong location.
 */
export const isMisdeliveredWrongLocation = (order: Order): boolean => {
  if (order?.packages?.length) {
    return order.packages.some(
      pkg =>
        pkg.eventLog[0]?.operation === packageEvents.MISDELIVERED &&
        pkg.eventLog[0]?.message?.toLowerCase()?.includes('wrong location')
    )
  }
  return false
}

/**
 * Returns true if the current time is on or after the scheduled service date for
 * any package in the order.
 */
export const onOrAfterScheduledServiceDate = (order: Order): boolean => {
  if (order?.packages?.length) {
    return order.packages.some(pkg => {
      const pkgTimeZone = pkg.timeZone
      const pkgScheduledServiceDate = DateTime.fromISO(pkg.scheduledServiceDate as string).setZone(pkgTimeZone!)
      const now = DateTime.now().setZone(pkgTimeZone!)
      return now.hasSame(pkgScheduledServiceDate, 'day') || now >= pkgScheduledServiceDate
    })
  }
  return false
}

/**
 * Maps a delivery issue status string to a user-facing header.
 */
export const packageHadDeliveryIssueHeader = (pkgHadDeliveryIssueStatus: string): string => {
  switch (pkgHadDeliveryIssueStatus) {
    case PackageDelayedIssue.wrongLocation:
    case PackageDelayedIssue.accessIssueFrontGate:
    case PackageDelayedIssue.accessIssueBuildingOrDoorCode:
    case PackageDelayedIssue.accessIssueSchoolCampus:
    case PackageDelayedIssue.closedLocationMailroom:
    case PackageDelayedIssue.closedLocationLeasingOffice:
    case PackageDelayedIssue.closedLocationOffice:
    case PackageDelayedIssue.closedLocationStoreFront:
    case PackageDelayedIssue.damagedPackage:
    case PackageDelayedIssue.militaryBase:
    case PackageDeliveryIssueV2.noAccessClosedDoorCode:
    case PackageDeliveryIssueV2.noAccessClosedLockedGate:
    case PackageDeliveryIssueV2.noAccessClosedClosedAfterHours:
    case PackageDeliveryIssueV2.noAccessClosedMilitaryBase:
    case PackageDeliveryIssueV2.cantLocateWrongLocation:
    case PackageDeliveryIssueV2.cantLocateMissingUnitNumber:
      return 'Delivery issue reported'
    case PackageDelayedIssue.unsafeConditionsWeatherRelated:
    case PackageDeliveryIssueV2.weather:
      return 'Delivery temporarily delayed'
    case PackageDelayedIssue.customerRefused:
    case PackageDeliveryIssueV2.customerRefused:
      return 'Delivery refused'
    default:
      return 'Delivery Delayed'
  }
}

/**
 * Maps a delivery issue status string to a user-facing subheader.
 */
export const packageHadDeliveryIssueSubheader = (pkgHadDeliveryIssueStatus: string): string => {
  switch (pkgHadDeliveryIssueStatus) {
    case PackageDelayedIssue.wrongLocation:
    case PackageDeliveryIssueV2.cantLocateWrongLocation:
    case PackageDeliveryIssueV2.cantLocateMissingUnitNumber:
      return 'Driver could not locate delivery address.'
    case PackageDelayedIssue.accessIssueFrontGate:
    case PackageDelayedIssue.accessIssueBuildingOrDoorCode:
    case PackageDelayedIssue.accessIssueSchoolCampus:
    case PackageDeliveryIssueV2.noAccessClosedDoorCode:
    case PackageDeliveryIssueV2.noAccessClosedLockedGate:
      return 'Driver could not access delivery address.'
    case PackageDelayedIssue.closedLocationMailroom:
    case PackageDelayedIssue.closedLocationLeasingOffice:
    case PackageDelayedIssue.closedLocationOffice:
    case PackageDelayedIssue.closedLocationStoreFront:
    case PackageDeliveryIssueV2.noAccessClosedClosedAfterHours:
      return 'Driver reported location closed when delivery was attempted'
    case PackageDelayedIssue.damagedPackage:
      return 'Driver reported damage to the package'
    case PackageDelayedIssue.customerRefused:
    case PackageDeliveryIssueV2.customerRefused:
      return 'Driver reported delivery was refused'
    case PackageDelayedIssue.militaryBase:
    case PackageDeliveryIssueV2.noAccessClosedMilitaryBase:
      return 'Unable to deliver to military base'
    case PackageDelayedIssue.unsafeConditionsWeatherRelated:
    case PackageDeliveryIssueV2.weather:
      return 'Unforeseen weather is impacting the speed of your delivery'
    case PackageDelayedIssue.unsafeConditionsDarkUnlitArea:
    case PackageDelayedIssue.unsafeConditionsPetOnSite:
    case PackageDeliveryIssueV2.safetyAnimalPet:
    case PackageDeliveryIssueV2.safetyUnlitArea:
    case PackageDeliveryIssueV2.safetyHostilePerson:
      return 'Driver reported issue during delivery'
    default:
      return 'Unforeseen issue during delivery route'
  }
}

/**
 * Returns a user-facing subheader for misdelivery events.
 */
export const misdeliverySubheader = (message: string): string => {
  switch (message) {
    case 'Lost in warehouse':
    case 'Lost by driver':
      return 'Unfortunately, your package has been lost in transit. We apologize for the inconvenience.'
    default:
      return 'It looks like something went wrong with this delivery. We apologize for the inconvenience.'
  }
}
